<div class="return-detail-container" *ngIf="returnData">

  <!-- Edit toggle (hidden for rolled-up plan members: view only) -->
  <button mat-button class="edit-values-link" (click)="toggleEdit()" *ngIf="!embeddedReadOnly">
    <mat-icon>edit</mat-icon> {{ editMode ? 'Editing values' : 'Edit Values' }}
  </button>

  <div class="reported-by" *ngIf="reportedByLabel">
    Reported by {{ reportedByLabel }}<ng-container *ngIf="reportedByAccountName"> — {{ reportedByAccountName }}</ng-container>
  </div>

  <!-- Header with Return Info -->
  <div class="return-header">
    <div class="header-info-cards">
      <div class="info-card">
        <div class="info-label">PERIOD</div>
        <div class="info-value">{{ returnData.period }}</div>
      </div>
      <div class="info-card">
        <div class="info-label">TYPE</div>
        <div class="info-value">{{ returnData.type }}</div>
      </div>
      <div class="info-card">
        <div class="info-label">FREQUENCY</div>
        <div class="info-value">{{ returnData.frequency }}</div>
      </div>
      <div class="info-card">
        <div class="info-label">DUE DATE</div>
        <div class="info-value">{{ returnData.dueDate | date }}</div>
      </div>
      <div class="info-card">
        <div class="info-label">STATUS</div>
        <div class="info-value">
          <span class="status-badge" [ngClass]="returnData.status">
            <span class="status-dot"></span>{{ returnData.status }}
          </span>
        </div>
      </div>
    </div>

    <div class="calculation-date">
      <mat-form-field appearance="outline">
        <mat-label>Calculation date</mat-label>
        <input matInput [matDatepicker]="picker" [(ngModel)]="returnData.calculationDate" [disabled]="!editMode" (dateChange)="onCalculationDateChange()">
        <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
        <mat-datepicker #picker></mat-datepicker>
      </mat-form-field>
    </div>
  </div>

  <!-- Main Content -->
  <div class="content-wrapper">
    <!-- Left Section - Return Calculation -->
    <div class="calculation-section">
      <div class="properties-table">
        <table>
          <thead>
            <tr>
              <th>Address</th>
              <th>Gross rental receipts</th>
              <th>Exemptions</th>
              <th>Exemption type / explanation</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let property of returnData.properties">
              <td>{{ property.address }}</td>
              <td>
                <input
                  type="number"
                  appMoney
                  class="currency-input"
                  [class.readonly]="!editMode"
                  [readonly]="!editMode"
                  [value]="property.grossRentalReceipts"
                  (input)="updatePropertyValue(property.id, 'grossRentalReceipts', +$any($event.target).value)"
                  step="0.01">
              </td>
              <td>
                <input
                  type="number"
                  appMoney
                  class="currency-input"
                  [class.readonly]="!editMode"
                  [readonly]="!editMode"
                  [value]="property.exemptions"
                  (input)="updatePropertyValue(property.id, 'exemptions', +$any($event.target).value)"
                  step="0.01">
              </td>
              <td>
                <div class="exempt-cell" *ngIf="property.exemptions > 0">
                  <select class="currency-input" [class.readonly]="!editMode" [disabled]="!editMode"
                          [(ngModel)]="property.exemptionTypeId" aria-label="Exemption type">
                    <option [ngValue]="null">Choose type…</option>
                    <option *ngFor="let t of exemptionTypes" [ngValue]="t.id">{{ t.name }}</option>
                  </select>
                  <input class="currency-input" [class.readonly]="!editMode" [readonly]="!editMode"
                         [(ngModel)]="property.exemptionExplanation" maxlength="500"
                         placeholder="Explanation" aria-label="Exemption explanation">
                </div>
              </td>
            </tr>
            <tr class="subtotal-row">
              <td><strong>Subtotal</strong></td>
              <td><strong>{{ getSubtotalGross() | currency }}</strong></td>
              <td><strong>{{ getSubtotalExemptions() | currency }}</strong></td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Summary Totals -->
      <div class="summary-card">
        <div class="summary-row">
          <span class="summary-label">Taxable Rental Receipts</span>
          <span class="summary-value">{{ returnData.taxableRentalReceipts | currency }}</span>
        </div>
        <div class="summary-row">
          <span class="summary-label">Total Taxes</span>
          <span class="summary-value">{{ returnData.totalTaxes | currency }}</span>
        </div>
        <div class="summary-row">
          <span class="summary-label">Late Fee</span>
          <span class="summary-value">{{ returnData.lateFee | currency }}</span>
        </div>
        <div class="summary-row">
          <span class="summary-label">Late Interest</span>
          <span class="summary-value">{{ returnData.lateInterest | currency }}</span>
        </div>
        <!-- Added fees were counted in the total but had no line of their own (QA 2026-09-30). -->
        @for (fee of extraFees; track $index) {
          <div class="summary-row">
            <span class="summary-label">{{ fee.label }}</span>
            <span class="summary-value">{{ fee.amount | currency }}</span>
          </div>
        }
        @if (returnData.credit) {
          <div class="summary-row">
            <span class="summary-label">Credit</span>
            <span class="summary-value">-{{ returnData.credit | currency }}</span>
          </div>
        }
        @for (a of allowanceLines; track $index) {
          <div class="summary-row">
            <span class="summary-label">{{ a.label }}</span>
            <span class="summary-value">-{{ a.amount | currency }}</span>
          </div>
        } @empty {
          <div class="summary-row">
            <span class="summary-label">Online Collection Allowance</span>
            <span class="summary-value">{{ 0 | currency }}</span>
          </div>
        }
        <div class="summary-row total-row">
          <span class="summary-label"><strong>Total Amount Due</strong></span>
          <span class="summary-value total-value"><strong>{{ returnData.totalAmountDue | currency }}</strong></span>
        </div>
      </div>
    </div>

    <!-- Right Section - Settings sidebar -->
    <div class="summary-section">
      <div class="sidebar-block">
        <div class="sidebar-header"><mat-icon>settings</mat-icon><span>Settings</span></div>
        <button mat-button class="sidebar-link" (click)="openSettings('fees')" *ngIf="!embeddedReadOnly">
          <mat-icon>attach_money</mat-icon>
          <span>Fee / Allowances ({{ chargeCount }})</span>
        </button>
      </div>

      <!-- Notes -->
      <div class="sidebar-block">
        <div class="sidebar-header"><mat-icon>note</mat-icon><span>Notes</span></div>
        <div class="sidebar-empty">
          @if (returnData.notes?.length) {
            <p class="empty-title">{{ returnData.notes?.length }} note(s)</p>
            <button mat-button class="link-action" (click)="openSettings('notes')">View notes</button>
          } @else {
            <p class="empty-title">No notes yet</p>
            <p class="empty-sub">Add notes to keep track of important information</p>
            <button mat-button class="link-action" (click)="openSettings('notes')"><mat-icon>add</mat-icon> Add Note</button>
          }
        </div>
      </div>

      <!-- History -->
      <div class="sidebar-block">
        <div class="sidebar-header"><mat-icon>history</mat-icon><span>History</span></div>
        <div class="sidebar-empty">
          @if (returnData.history?.length) {
            <p class="empty-title">{{ returnData.history?.length }} change(s)</p>
            <button mat-button class="link-action" (click)="openSettings('history')">View history</button>
          } @else {
            <p class="empty-title">No changes recorded</p>
            <p class="empty-sub">All modifications to this return will be tracked here automatically</p>
          }
        </div>
      </div>

      <!-- Attachments -->
      <div class="sidebar-block">
        <div class="sidebar-header"><mat-icon>attach_file</mat-icon><span>Attachments</span></div>
        <div class="sidebar-empty">
          @if (returnData.attachments?.length) {
            <p class="empty-title">{{ returnData.attachments?.length }} file(s)</p>
            <button mat-button class="link-action" (click)="openSettings('attachments')">View files</button>
          } @else {
            <p class="empty-title">No files attached</p>
            <p class="empty-sub">Upload documents, receipts or other files related to this return</p>
            <button mat-button class="link-action" (click)="openSettings('attachments')"><mat-icon>upload</mat-icon> Upload File</button>
          }
        </div>
      </div>
    </div>
  </div>

  <!-- Action Buttons -->
  <div class="action-buttons">
    @if (savedDirty && !saving) {
      <span class="saved-hint"><mat-icon>check_circle</mat-icon> Saved</span>
    }
    <button mat-stroked-button color="primary" (click)="goBack()">
      <mat-icon>arrow_back</mat-icon> GO BACK
    </button>
    <button mat-raised-button color="primary" (click)="save()" [disabled]="saving" *ngIf="!embeddedReadOnly">
      <mat-icon>save</mat-icon> {{ saving ? 'SAVING…' : 'SAVE' }}
    </button>
  </div>
</div>
